import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiInput from 'components/Ui/UiInput'
import UiTextarea from 'components/Ui/UiTextarea'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hasValidPlaceholderSyntax, HtmlTemplateBlock, readHtmlBlocks } from 'utils/email/htmlTemplate'

interface Props {
    value: string
    onChange: (value: string) => void
    onBlur: () => void
    onFocus: () => void
    inputRef: (element: HTMLTextAreaElement | null) => void
    error?: string
    storageScope: string
}

export default function HtmlTemplateEditor({ value, onChange, onBlur, onFocus, inputRef, error, storageScope }: Props) {
    const { t } = useTranslation()
    const key = `crm-email-html-blocks:v1:${storageScope}`
    const editor = useRef<HTMLTextAreaElement | null>(null)
    const [initial] = useState(() => {
        try {
            return { blocks: readHtmlBlocks(key), failed: false }
        } catch {
            return { blocks: [], failed: true }
        }
    })
    const [blocks, setBlocks] = useState<HtmlTemplateBlock[]>(initial.blocks)
    const [name, setName] = useState('')
    const [message, setMessage] = useState('')
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

    const persist = (next: HtmlTemplateBlock[]) => {
        if (initial.failed) return false
        try {
            if (JSON.stringify(readHtmlBlocks(key)) !== JSON.stringify(blocks)) {
                setMessage(t('Blocks changed in another window. Reopen the editor to load them.'))
                return false
            }
            localStorage.setItem(key, JSON.stringify(next))
            setBlocks(next)
            setMessage('')
            return true
        } catch {
            setMessage(t('Could not save blocks. Browser storage is unavailable or full.'))
            return false
        }
    }

    const saveBlock = () => {
        const input = editor.current
        const html =
            input && input.selectionStart !== input.selectionEnd
                ? value.slice(input.selectionStart, input.selectionEnd)
                : value
        if (!name.trim() || !html.trim()) {
            setMessage(t('Enter a block name and select HTML, or save the entire content.'))
            return
        }
        if (!hasValidPlaceholderSyntax(html)) {
            setMessage(t('Invalid placeholder syntax. Use double braces around a field name.'))
            return
        }
        if (blocks.some((block) => block.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase())) {
            setMessage(t('A block with this name already exists. Choose another name.'))
            return
        }
        if (persist([...blocks, { id: crypto.randomUUID(), name: name.trim(), html }])) setName('')
    }

    const insert = (html: string) => {
        const start = editor.current?.selectionStart ?? value.length
        const end = editor.current?.selectionEnd ?? start
        onChange(value.slice(0, start) + html + value.slice(end))
        requestAnimationFrame(() => {
            editor.current?.focus()
            editor.current?.setSelectionRange(start + html.length, start + html.length)
        })
    }

    return (
        <div className="space-y-3">
            <label htmlFor="email-template-html" className="block text-sm font-medium">
                {t('HTML source')}
            </label>
            <p id="email-template-html-help" className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                    'Paste HTML from your editor. Styles are preserved. Placeholder names will be verified when the backend catalogue is available.'
                )}
            </p>
            <UiTextarea
                id="email-template-html"
                ref={(element) => {
                    editor.current = element
                    inputRef(element)
                }}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                onFocus={onFocus}
                spellCheck={false}
                aria-invalid={Boolean(error)}
                aria-describedby={`email-template-html-help${error ? ' email-template-html-error' : ''}`}
                className="min-h-100 font-mono"
            />
            {error && (
                <p id="email-template-html-error" role="alert" className="text-sm text-red-600">
                    {error}
                </p>
            )}
            <details className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                <summary className="cursor-pointer font-medium focus-visible:outline-2">
                    {t('Reusable HTML blocks')} ({blocks.length})
                </summary>
                <p className="my-3 text-sm text-gray-600 dark:text-gray-400">
                    {t('Saved only in this browser for your user. Blocks are not shared or backed up on the server.')}
                </p>
                {initial.failed && (
                    <p role="alert">{t('Could not read local blocks. Existing storage has not been overwritten.')}</p>
                )}
                <label htmlFor="email-block-name" className="block text-sm">
                    {t('Block name')}
                </label>
                <div className="my-2 flex flex-wrap gap-2">
                    <UiInput
                        id="email-block-name"
                        maxLength={120}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    <UiButton
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Secondary}
                        type="button"
                        disabled={initial.failed}
                        onClick={saveBlock}
                    >
                        {t('Save selection or entire HTML as block')}
                    </UiButton>
                </div>
                {message && (
                    <p role="alert" className="text-sm text-red-600">
                        {message}
                    </p>
                )}
                <ul className="space-y-2">
                    {blocks.map((block) => (
                        <li key={block.id} className="flex flex-wrap items-center gap-2">
                            <span className="min-w-0 flex-1 break-words">{block.name}</span>
                            <UiButton
                                size={UiButtonSize.Small}
                                style={UiButtonStyle.Secondary}
                                type="button"
                                onClick={() => insert(block.html)}
                                aria-label={`${t('Insert block')}: ${block.name}`}
                            >
                                {t('Insert block')}
                            </UiButton>
                            {confirmDelete === block.id ? (
                                <>
                                    <span>{t('Delete this local block?')}</span>
                                    <UiButton
                                        size={UiButtonSize.Small}
                                        style={UiButtonStyle.DELETE}
                                        type="button"
                                        onClick={() => {
                                            if (persist(blocks.filter((item) => item.id !== block.id)))
                                                setConfirmDelete(null)
                                        }}
                                    >
                                        {t('Confirm deletion')}
                                    </UiButton>
                                    <UiButton
                                        size={UiButtonSize.Small}
                                        style={UiButtonStyle.Secondary}
                                        type="button"
                                        onClick={() => setConfirmDelete(null)}
                                    >
                                        {t('Cancel')}
                                    </UiButton>
                                </>
                            ) : (
                                <UiButton
                                    size={UiButtonSize.Small}
                                    style={UiButtonStyle.Secondary}
                                    type="button"
                                    onClick={() => setConfirmDelete(block.id)}
                                    aria-label={`${t('Delete block')}: ${block.name}`}
                                >
                                    {t('Delete block')}
                                </UiButton>
                            )}
                        </li>
                    ))}
                </ul>
            </details>
        </div>
    )
}
